// src/components/Navbar2.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";

import { IoClose } from "react-icons/io5";
import { HiOutlineMenuAlt3 } from "react-icons/hi";

import {
  FaArrowRight,
  FaChevronDown,
  FaChevronRight,
  FaCog,
  FaExternalLinkAlt,
  FaMoon,
  FaPlayCircle,
  FaSignOutAlt,
  FaSun,
  FaUser,
} from "react-icons/fa";

import useNavbarAppearance from "./navbar/hooks/useNavbarAppearance";

import NavbarBrand from "./navbar/ui/NavbarBrand";
import NavbarCta from "./navbar/ui/NavbarCta";

import {
  cn,
  isExternalUrl,
} from "./navbar/utils/navbarUtils";

const FALLBACK_AVATAR =
  "https://www.monappliclub.com/images/LV.webp";

const cx = cn;

function useOnClickOutside(ref, handler, enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;

    const handlePointerDown = (event) => {
      if (!ref.current) return;
      if (ref.current.contains(event.target)) return;

      handler(event);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [enabled, handler, ref]);
}

function SmartLink({
  to,
  children,
  className,
  onClick,
  ...props
}) {
  if (isExternalUrl(to)) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className={className}
        {...props}
      >
        {children}
      </a>
    );
  }

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={className}
      {...props}
    >
      {children}
    </NavLink>
  );
}

function DesktopMenuButton({
  label,
  isOpen,
  onClick,
  transparent = false,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "group relative inline-flex h-11 items-center gap-2 px-3",
        "text-sm font-medium transition-colors duration-300",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ff7900]",
        transparent
          ? isOpen
            ? "text-white"
            : "text-white/75 hover:text-white"
          : isOpen
            ? "text-[#0868f2] dark:text-blue-300"
            : [
                "text-slate-700 hover:text-[#0868f2]",
                "dark:text-slate-200 dark:hover:text-blue-300",
              ],
      )}
      aria-expanded={isOpen}
      aria-haspopup="menu"
    >
      <span>{label}</span>

      <FaChevronDown
        className={cx(
          "text-[10px] transition-transform duration-300",
          isOpen && "rotate-180",
        )}
      />

      <span
        className={cx(
          "absolute inset-x-3 bottom-1 h-px origin-left",
          "transition-transform duration-300",
          transparent
            ? "bg-white"
            : "bg-[#0868f2] dark:bg-blue-300",
          isOpen
            ? "scale-x-100"
            : "scale-x-0 group-hover:scale-x-100",
        )}
      />
    </button>
  );
}

function DesktopApplicationsPanel({
  productGroups,
  onClose,
  onContactClick,
  onDemoClick,
}) {
  return (
    <div
      className={cx(
        "overflow-hidden rounded-[24px]",
        "border border-slate-200 bg-white",
        "shadow-[0_30px_80px_-35px_rgba(15,23,42,0.55)]",
        "dark:border-white/10 dark:bg-slate-950",
      )}
      role="menu"
    >
      <div className="border-b border-slate-200 px-6 py-5 dark:border-white/10">
        <div className="flex items-center justify-between gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#ff7900]">
              Applications MonAppliClub
            </p>

            <h3 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">
              Des outils adaptés à chaque structure
            </h3>
          </div>

          <button
            type="button"
            onClick={() => {
              onClose();
              onDemoClick();
            }}
            className={cx(
              "hidden h-10 shrink-0 items-center gap-2 rounded-full px-4",
              "border border-slate-200 text-sm font-semibold text-slate-700",
              "transition hover:border-[#0868f2]/30 hover:text-[#0868f2]",
              "dark:border-white/10 dark:text-slate-200",
              "dark:hover:border-blue-400/30 dark:hover:text-blue-300",
              "lg:inline-flex",
            )}
          >
            <FaPlayCircle />
            Voir la démo
          </button>
        </div>
      </div>

      <div className="grid max-h-[calc(100vh-190px)] overflow-y-auto lg:grid-cols-4">
        {productGroups.map((group, groupIndex) => (
          <section
            key={group.title}
            className={cx(
              "px-5 py-6",
              groupIndex > 0 &&
                "border-t border-slate-200 dark:border-white/10 lg:border-l lg:border-t-0",
            )}
          >
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#0868f2] dark:text-blue-300">
              {group.title}
            </p>

            <div className="space-y-1">
              {group.items.map((item) => (
                <SmartLink
                  key={`${group.title}-${item.label}`}
                  to={item.to}
                  onClick={onClose}
                  className={cx(
                    "group block border-b border-slate-100 py-3",
                    "transition last:border-b-0",
                    "dark:border-white/5",
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 transition group-hover:text-[#0868f2] dark:text-white dark:group-hover:text-blue-300">
                          {item.label}
                        </span>

                        {isExternalUrl(item.to) ? (
                          <FaExternalLinkAlt className="shrink-0 text-[9px] text-slate-400" />
                        ) : null}
                      </div>

                      {item.desc ? (
                        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                          {item.desc}
                        </p>
                      ) : null}
                    </div>

                    <FaArrowRight className="mt-1 shrink-0 text-[10px] text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-[#ff7900] dark:text-slate-600" />
                  </div>
                </SmartLink>
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="flex flex-col gap-4 border-t border-slate-200 bg-slate-50 px-6 py-5 dark:border-white/10 dark:bg-slate-900/70 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Vous ne savez pas quelle application correspond à votre besoin ?
        </p>

        <button
          type="button"
          onClick={() => {
            onClose();
            onContactClick();
          }}
          className={cx(
            "group inline-flex h-10 shrink-0 items-center justify-center gap-2",
            "rounded-full bg-[#0868f2] px-5 text-sm font-semibold text-white",
            "transition hover:bg-[#0648b8]",
          )}
        >
          Être conseillé
          <FaArrowRight className="text-[10px] transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </div>
  );
}

function DesktopPricingPanel({
  pricingPanels,
  onClose,
  onContactClick,
}) {
  return (
    <div
      className={cx(
        "overflow-hidden rounded-[24px]",
        "border border-slate-200 bg-white",
        "shadow-[0_30px_80px_-35px_rgba(15,23,42,0.55)]",
        "dark:border-white/10 dark:bg-slate-950",
      )}
      role="menu"
    >
      <div className="grid lg:grid-cols-[0.8fr_1.2fr]">
        <div className="bg-[#0868f2] px-7 py-8 text-white">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/65">
            Des tarifs lisibles
          </p>

          <h3 className="mt-4 max-w-md text-3xl font-semibold leading-tight tracking-tight">
            Une formule adaptée à votre structure.
          </h3>

          <p className="mt-4 max-w-md text-sm leading-7 text-white/75">
            Clubs, associations, collectivités, comités et ligues bénéficient
            d’offres simples, sans mauvaise surprise.
          </p>

          <div className="mt-8 border-t border-white/20 pt-6">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/60">
              Offre MonAppliClub Standard
            </p>

            <div className="mt-2 flex items-end gap-2">
              <span className="text-4xl font-semibold">360 €</span>
              <span className="pb-1 text-sm text-white/70">par an</span>
            </div>

            <p className="mt-2 text-sm text-white/75">
              Tarif unique, quel que soit le nombre d’adhérents.
            </p>
          </div>
        </div>

        <div className="px-7 py-7">
          <div>
            {pricingPanels.map((item, index) => (
              <SmartLink
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={cx(
                  "group flex items-center justify-between gap-5 py-5",
                  index > 0 &&
                    "border-t border-slate-200 dark:border-white/10",
                )}
              >
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#ff7900]">
                    {item.kicker}
                  </p>

                  <h4 className="mt-1 text-lg font-semibold text-slate-950 transition group-hover:text-[#0868f2] dark:text-white dark:group-hover:text-blue-300">
                    {item.label}
                  </h4>

                  <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    {item.desc}
                  </p>
                </div>

                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-slate-200 text-[#0868f2] transition group-hover:border-[#0868f2] group-hover:bg-[#0868f2] group-hover:text-white dark:border-white/10 dark:text-blue-300">
                  <FaArrowRight className="text-[10px]" />
                </span>
              </SmartLink>
            ))}
          </div>

          <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-5 dark:border-white/10 sm:flex-row">
            <SmartLink
              to="/tarifs"
              onClick={onClose}
              className={cx(
                "inline-flex h-11 flex-1 items-center justify-center gap-2",
                "rounded-full bg-[#0868f2] px-5 text-sm font-semibold text-white",
                "transition hover:bg-[#0648b8]",
              )}
            >
              Voir tous les tarifs
              <FaArrowRight className="text-[10px]" />
            </SmartLink>

            <button
              type="button"
              onClick={() => {
                onClose();
                onContactClick();
              }}
              className={cx(
                "inline-flex h-11 flex-1 items-center justify-center",
                "rounded-full border border-slate-200 px-5",
                "text-sm font-semibold text-slate-700",
                "transition hover:border-[#ff7900] hover:text-[#ff7900]",
                "dark:border-white/10 dark:text-slate-200",
              )}
            >
              Demander conseil
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserAvatar({ userInfo, size = "default" }) {
  return (
    <img
      src={userInfo?.photoURL || FALLBACK_AVATAR}
      alt="Avatar utilisateur"
      className={cx(
        "rounded-full object-cover",
        size === "small" ? "h-9 w-9" : "h-10 w-10",
      )}
    />
  );
}

function MobileNavItem({
  item,
  openMobileSubmenu,
  setOpenMobileSubmenu,
  onClose,
}) {
  if (item.type === "action") {
    return (
      <button
        type="button"
        onClick={() => {
          onClose();
          item.onClick?.();
        }}
        className={cx(
          "group flex w-full items-center justify-between",
          "border-b border-slate-200 py-4 text-left",
          "text-base font-semibold text-slate-900",
          "transition hover:text-[#0868f2]",
          "dark:border-white/10 dark:text-white",
          "dark:hover:text-blue-300",
        )}
      >
        <span>{item.label}</span>

        <FaArrowRight className="text-xs text-[#ff7900] transition-transform group-hover:translate-x-1" />
      </button>
    );
  }

  if (item.type !== "submenu") {
    return (
      <SmartLink
        to={item.href}
        onClick={onClose}
        className={cx(
          "group flex items-center justify-between",
          "border-b border-slate-200 py-4",
          "text-base font-semibold text-slate-900",
          "transition hover:text-[#0868f2]",
          "dark:border-white/10 dark:text-white",
          "dark:hover:text-blue-300",
        )}
      >
        <span>{item.label}</span>

        <FaArrowRight className="text-xs text-[#ff7900] transition-transform group-hover:translate-x-1" />
      </SmartLink>
    );
  }

  const isOpen = openMobileSubmenu === item.key;

  return (
    <div className="border-b border-slate-200 dark:border-white/10">
      <button
        type="button"
        onClick={() => {
          setOpenMobileSubmenu((previous) =>
            previous === item.key ? null : item.key,
          );
        }}
        className={cx(
          "flex w-full items-center justify-between py-4 text-left",
          "text-base font-semibold text-slate-900",
          "transition hover:text-[#0868f2]",
          "dark:text-white dark:hover:text-blue-300",
        )}
        aria-expanded={isOpen}
      >
        <span>{item.label}</span>

        <FaChevronRight
          className={cx(
            "text-xs text-[#ff7900] transition-transform duration-300",
            isOpen && "rotate-90",
          )}
        />
      </button>

      {isOpen ? (
        <div className="pb-5">
          <div className="space-y-6 border-l border-slate-200 pl-4 dark:border-white/10">
            {item.groups.map((group) => (
              <section key={group.title}>
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0868f2] dark:text-blue-300">
                  {group.title}
                </p>

                <div className="space-y-1">
                  {group.items.map((child) => (
                    <SmartLink
                      key={`${group.title}-${child.label}`}
                      to={child.to}
                      onClick={onClose}
                      className="group block py-2"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-800 transition group-hover:text-[#0868f2] dark:text-slate-200 dark:group-hover:text-blue-300">
                            {child.label}
                          </p>

                          {child.desc ? (
                            <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                              {child.desc}
                            </p>
                          ) : null}
                        </div>

                        {isExternalUrl(child.to) ? (
                          <FaExternalLinkAlt className="mt-1 shrink-0 text-[9px] text-slate-400" />
                        ) : (
                          <FaArrowRight className="mt-1 shrink-0 text-[9px] text-[#ff7900]" />
                        )}
                      </div>
                    </SmartLink>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function Navbar2() {
  const navigate = useNavigate();
  const location = useLocation();

  const navMenuRef = useRef(null);
  const userMenuRef = useRef(null);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDesktopSubmenu, setOpenDesktopSubmenu] = useState(null);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null);
  const [openUserMenu, setOpenUserMenu] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const [userInfo, setUserInfo] = useState({
    photoURL: FALLBACK_AVATAR,
    nom: "",
    prenom: "",
    role_id: null,
  });

  const forceSolidNavbar =
    Boolean(openDesktopSubmenu) ||
    openUserMenu ||
    mobileOpen;

  const { isTransparent } = useNavbarAppearance({
    pathname: location.pathname,
    forceSolid: forceSolidNavbar,
  });

  const productGroups = useMemo(
    () => [
      {
        title: "Clubs & sport",
        items: [
          {
            to: "/product-monappliclub",
            label: "MonAppliClub",
            desc: "Gestion complète de club sportif.",
          },
          {
            to: "/product-monappliclub-light",
            label: "MonAppliClub Light",
            desc: "L’essentiel pour démarrer simplement.",
          },
          {
            to: "/product-monappliclub-tournoi",
            label: "MonAppliTournoi",
            desc: "Tournois, tableaux et inscriptions.",
          },
          {
            to: "/product-judocoachpro",
            label: "Judo Coach Pro",
            desc: "Suivi sportif et progression.",
          },
          {
            to: "/product-judopresencepro",
            label: "Judo Présence Pro",
            desc: "Suivi des présences.",
          },
        ],
      },
      {
        title: "Associations & collectivités",
        items: [
          {
            to: "/product-monapplimairie",
            label: "MonAppliMairie",
            desc: "Site de mairie facile à administrer.",
          },
          {
            to: "/product-monappliclub-omnisports",
            label: "MonAppliAsso",
            desc: "Associations, activités, lieux et contacts.",
          },
          {
            to: "/monappliag",
            label: "MonAppliAG",
            desc: "Assemblées générales et votes.",
          },
        ],
      },
      {
        title: "Comités & ligues",
        items: [
          {
            to: "https://www.judogironde.fr/",
            label: "MonAppliComité",
            desc: "Une plateforme pour les comités départementaux.",
          },
          {
            to: "https://www.liguejudona.com/",
            label: "MonAppliLigue",
            desc: "Communication et gestion régionale.",
          },
        ],
      },
      {
        title: "Outils judo",
        items: [
          {
            to: "/monappliscore",
            label: "MonAppliScore",
            desc: "Tableau de score Judo gratuit.",
          },
          {
            to: "/partenaires-kata",
            label: "MonAppliKata",
            desc: "Trouver un partenaire de kata.",
          },
          {
            to: "/monapplicate",
            label: "MonAppliCaté",
            desc: "Catégories d’âge et de poids.",
          },
          {
            to: "https://www.randori-cards.com/",
            label: "Randori Cards",
            desc: "Jeu de cartes inspiré du judo.",
          },
        ],
      },
    ],
    [],
  );

  const pricingPanels = useMemo(
    () => [
      {
        to: "/tarifs#clubs",
        kicker: "Clubs sportifs",
        label: "Offres Club",
        desc: "Adhérents, cotisations, documents, communication et événements.",
      },
      {
        to: "/tarifs#instances",
        kicker: "Comités & Ligues",
        label: "Offres Instances",
        desc: "Des solutions pensées pour les structures territoriales.",
      },
      {
        to: "/tarifs#sur-mesure",
        kicker: "Projet spécifique",
        label: "Développement sur mesure",
        desc: "Une application adaptée à votre fonctionnement.",
      },
    ],
    [],
  );

  const tariffGroups = useMemo(
    () => [
      {
        title: "Tarifs",
        items: pricingPanels.map((item) => ({
          to: item.to,
          label: item.label,
          desc: item.desc,
        })),
      },
    ],
    [pricingPanels],
  );

  useEffect(() => {
    const syncAuthentication = () => {
      try {
        const token = localStorage.getItem("token");
        const storedUser = JSON.parse(
          localStorage.getItem("userInfo") || "null",
        );

        if (token && storedUser) {
          setIsAuthenticated(true);

          setUserInfo({
            photoURL: storedUser.photoURL || FALLBACK_AVATAR,
            nom: storedUser.nom || "",
            prenom: storedUser.prenom || "",
            role_id: storedUser.role_id ?? null,
          });

          return;
        }

        setIsAuthenticated(false);

        setUserInfo({
          photoURL: FALLBACK_AVATAR,
          nom: "",
          prenom: "",
          role_id: null,
        });
      } catch (error) {
        console.error(
          "Impossible de synchroniser l’authentification :",
          error,
        );

        setIsAuthenticated(false);
      }
    };

    syncAuthentication();

    window.addEventListener("storage", syncAuthentication);

    return () => {
      window.removeEventListener("storage", syncAuthentication);
    };
  }, []);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");

    const prefersDarkMode = window.matchMedia?.(
      "(prefers-color-scheme: dark)",
    )?.matches;

    const shouldUseDarkMode = savedTheme
      ? savedTheme === "dark"
      : Boolean(prefersDarkMode);

    setIsDarkMode(shouldUseDarkMode);

    document.documentElement.classList.toggle(
      "dark",
      shouldUseDarkMode,
    );
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDesktopSubmenu(null);
    setOpenMobileSubmenu(null);
    setOpenUserMenu(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== "Escape") return;

      setMobileOpen(false);
      setOpenDesktopSubmenu(null);
      setOpenMobileSubmenu(null);
      setOpenUserMenu(false);
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  useOnClickOutside(
    navMenuRef,
    () => setOpenDesktopSubmenu(null),
    Boolean(openDesktopSubmenu),
  );

  useOnClickOutside(
    userMenuRef,
    () => setOpenUserMenu(false),
    openUserMenu,
  );

  const toggleDarkMode = () => {
    const nextValue = !isDarkMode;

    setIsDarkMode(nextValue);

    document.documentElement.classList.toggle(
      "dark",
      nextValue,
    );

    localStorage.setItem(
      "theme",
      nextValue ? "dark" : "light",
    );
  };

  const closeAllMenus = () => {
    setMobileOpen(false);
    setOpenDesktopSubmenu(null);
    setOpenMobileSubmenu(null);
    setOpenUserMenu(false);
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);

    if (!section) return false;

    section.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    return true;
  };

  const handleHomeClick = () => {
    closeAllMenus();

    if (location.pathname === "/") {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return;
    }

    navigate("/");

    window.setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 120);
  };

  const handleContactClick = () => {
    closeAllMenus();

    if (location.pathname === "/") {
      scrollToSection("contact");
      return;
    }

    navigate("/");

    window.setTimeout(() => {
      scrollToSection("contact");
    }, 180);
  };

  const handleDemoClick = () => {
    closeAllMenus();

    window.open(
      "https://www.monappliclub-demo.com",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userInfo");

    setIsAuthenticated(false);

    setUserInfo({
      photoURL: FALLBACK_AVATAR,
      nom: "",
      prenom: "",
      role_id: null,
    });

    closeAllMenus();
    navigate("/");
  };

  const navItems = useMemo(
    () => [
      {
        label: "Accueil",
        type: "action",
        onClick: handleHomeClick,
      },
      {
        label: "Applications",
        key: "applications",
        type: "submenu",
        groups: productGroups,
      },
      {
        label: "Tarifs",
        key: "tarifs",
        type: "submenu",
        groups: tariffGroups,
      },
      {
        label: "Contact",
        type: "action",
        onClick: handleContactClick,
      },
    ],
    [productGroups, tariffGroups],
  );

  const canAccessAdmin = isAuthenticated;

  return (
    <header
      className={cx(
        "fixed inset-x-0 top-0 z-50 w-full",
        "transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300",
        isTransparent
          ? "border-b border-white/15 bg-transparent shadow-none"
          : [
              "border-b border-slate-200/80",
              "bg-white/95 text-slate-900",
              "shadow-[0_12px_32px_-24px_rgba(15,23,42,0.55)]",
              "backdrop-blur-xl",
              "dark:border-white/10 dark:bg-slate-950/95 dark:text-white",
            ],
      )}
    >
      <div className="mx-auto flex h-[76px] max-w-[1480px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <div className="shrink-0">
          <NavbarBrand
            onClick={handleHomeClick}
            transparent={isTransparent}
          />
        </div>

        <div
          ref={navMenuRef}
          className="relative hidden flex-1 items-center justify-center xl:flex"
        >
          <nav className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleHomeClick}
              className={cx(
                "group relative inline-flex h-11 items-center px-3",
                "text-sm font-medium transition-colors duration-300",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ff7900]",
                isTransparent
                  ? "text-white"
                  : "text-[#0868f2] dark:text-blue-300",
              )}
            >
              Accueil

              <span
                className={cx(
                  "absolute inset-x-3 bottom-1 h-px",
                  isTransparent
                    ? "bg-white"
                    : "bg-[#0868f2] dark:bg-blue-300",
                )}
              />
            </button>

            <DesktopMenuButton
              label="Applications"
              transparent={isTransparent}
              isOpen={openDesktopSubmenu === "applications"}
              onClick={() => {
                setOpenDesktopSubmenu((previous) =>
                  previous === "applications"
                    ? null
                    : "applications",
                );

                setOpenUserMenu(false);
              }}
            />

            <DesktopMenuButton
              label="Tarifs"
              transparent={isTransparent}
              isOpen={openDesktopSubmenu === "tarifs"}
              onClick={() => {
                setOpenDesktopSubmenu((previous) =>
                  previous === "tarifs" ? null : "tarifs",
                );

                setOpenUserMenu(false);
              }}
            />

            <button
              type="button"
              onClick={handleContactClick}
              className={cx(
                "group relative inline-flex h-11 items-center px-3",
                "text-sm font-medium transition-colors duration-300",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ff7900]",
                isTransparent
                  ? "text-white/75 hover:text-white"
                  : [
                      "text-slate-700 hover:text-[#0868f2]",
                      "dark:text-slate-200 dark:hover:text-blue-300",
                    ],
              )}
            >
              Contact

              <span
                className={cx(
                  "absolute inset-x-3 bottom-1 h-px origin-left scale-x-0",
                  "transition-transform duration-300 group-hover:scale-x-100",
                  isTransparent
                    ? "bg-white"
                    : "bg-[#0868f2] dark:bg-blue-300",
                )}
              />
            </button>
          </nav>

          {openDesktopSubmenu ? (
            <div className="absolute left-1/2 top-[calc(100%+14px)] w-[min(1120px,calc(100vw-32px))] -translate-x-1/2">
              {openDesktopSubmenu === "applications" ? (
                <DesktopApplicationsPanel
                  productGroups={productGroups}
                  onClose={() => setOpenDesktopSubmenu(null)}
                  onContactClick={handleContactClick}
                  onDemoClick={handleDemoClick}
                />
              ) : null}

              {openDesktopSubmenu === "tarifs" ? (
                <DesktopPricingPanel
                  pricingPanels={pricingPanels}
                  onClose={() => setOpenDesktopSubmenu(null)}
                  onContactClick={handleContactClick}
                />
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden lg:block">
            <NavbarCta
              onClick={handleContactClick}
              transparent={isTransparent}
            />
          </div>

          <button
            type="button"
            onClick={toggleDarkMode}
            className={cx(
              "grid h-10 w-10 place-items-center rounded-full border",
              "transition-all duration-300",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ff7900]",
              isTransparent
                ? [
                    "border-white/25 bg-white/10 text-white",
                    "backdrop-blur-md hover:bg-white/20",
                  ]
                : [
                    "border-slate-200 bg-white text-slate-600",
                    "hover:border-[#0868f2]/30 hover:text-[#0868f2]",
                    "dark:border-white/10 dark:bg-slate-900",
                    "dark:text-slate-200 dark:hover:bg-slate-800",
                  ],
            )}
            aria-label={
              isDarkMode
                ? "Activer le mode clair"
                : "Activer le mode sombre"
            }
          >
            {isDarkMode ? (
              <FaSun className="text-[#ff7900]" />
            ) : (
              <FaMoon />
            )}
          </button>

          {isAuthenticated ? (
            <div
              ref={userMenuRef}
              className="relative hidden lg:block"
            >
              <button
                type="button"
                onClick={() => {
                  setOpenUserMenu((previous) => !previous);
                  setOpenDesktopSubmenu(null);
                }}
                className={cx(
                  "flex h-11 items-center gap-2 rounded-full border",
                  "px-1.5 pr-3 transition-all duration-300",
                  isTransparent
                    ? [
                        "border-white/25 bg-white/10 text-white",
                        "backdrop-blur-md hover:bg-white/20",
                      ]
                    : [
                        "border-slate-200 bg-white text-slate-800",
                        "hover:border-[#0868f2]/30",
                        "dark:border-white/10 dark:bg-slate-900",
                        "dark:text-white dark:hover:bg-slate-800",
                      ],
                )}
                aria-haspopup="menu"
                aria-expanded={openUserMenu}
              >
                <UserAvatar
                  userInfo={userInfo}
                  size="small"
                />

                <span className="max-w-[135px] truncate text-sm font-semibold">
                  {userInfo.prenom || userInfo.nom
                    ? `${userInfo.prenom} ${userInfo.nom}`.trim()
                    : "Compte"}
                </span>

                <FaChevronDown
                  className={cx(
                    "text-[10px] transition-transform",
                    isTransparent
                      ? "text-white/60"
                      : "text-slate-400",
                    openUserMenu && "rotate-180",
                  )}
                />
              </button>

              {openUserMenu ? (
                <div
                  className={cx(
                    "absolute right-0 top-[calc(100%+12px)] w-72",
                    "overflow-hidden rounded-[20px]",
                    "border border-slate-200 bg-white shadow-2xl",
                    "dark:border-white/10 dark:bg-slate-950",
                  )}
                  role="menu"
                >
                  <div className="border-b border-slate-200 p-4 dark:border-white/10">
                    <div className="flex items-center gap-3">
                      <UserAvatar userInfo={userInfo} />

                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-950 dark:text-white">
                          {userInfo.prenom} {userInfo.nom}
                        </p>

                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          Session active
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2">
                    {canAccessAdmin ? (
                      <Link
                        to="/appli/site-activity"
                        onClick={closeAllMenus}
                        className={cx(
                          "flex items-center gap-3 rounded-xl px-4 py-3",
                          "text-sm font-semibold text-slate-700",
                          "transition hover:bg-blue-50 hover:text-[#0868f2]",
                          "dark:text-slate-200 dark:hover:bg-white/5",
                          "dark:hover:text-blue-300",
                        )}
                      >
                        <FaCog />
                        Administration
                      </Link>
                    ) : null}

                    <button
                      type="button"
                      onClick={handleLogout}
                      className={cx(
                        "flex w-full items-center gap-3 rounded-xl px-4 py-3",
                        "text-left text-sm font-semibold text-red-600",
                        "transition hover:bg-red-50",
                        "dark:hover:bg-red-950/25",
                      )}
                    >
                      <FaSignOutAlt />
                      Déconnexion
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => {
              setMobileOpen(true);
              setOpenDesktopSubmenu(null);
              setOpenUserMenu(false);
            }}
            className={cx(
              "grid h-11 w-11 place-items-center rounded-full",
              "transition-all duration-300 xl:hidden",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ff7900]",
              isTransparent
                ? "text-white hover:bg-white/10"
                : [
                    "text-[#0868f2] hover:bg-blue-50",
                    "dark:text-blue-300 dark:hover:bg-white/10",
                  ],
            )}
            aria-label="Ouvrir le menu"
            aria-expanded={mobileOpen}
          >
            <HiOutlineMenuAlt3 className="text-[28px]" />
          </button>
        </div>
      </div>

      {mobileOpen &&
        createPortal(
          <div className="fixed inset-0 z-[99999] bg-slate-950/45 backdrop-blur-sm xl:hidden">
            <div
              className={cx(
                "ml-auto flex h-[100dvh] w-full max-w-[440px] flex-col",
                "bg-white shadow-2xl dark:bg-slate-950",
              )}
              style={{
                paddingTop: "env(safe-area-inset-top)",
                paddingBottom: "env(safe-area-inset-bottom)",
              }}
            >
              <div className="flex h-[76px] items-center justify-between border-b border-slate-200 px-5 dark:border-white/10">
                <NavbarBrand
                  onClick={handleHomeClick}
                  compact
                />

                <button
                  type="button"
                  onClick={closeAllMenus}
                  className={cx(
                    "grid h-10 w-10 place-items-center rounded-full",
                    "text-slate-900 transition hover:bg-slate-100",
                    "dark:text-white dark:hover:bg-white/10",
                  )}
                  aria-label="Fermer le menu"
                >
                  <IoClose className="text-3xl" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4">
                <nav>
                  {navItems.map((item) => (
                    <MobileNavItem
                      key={item.label}
                      item={item}
                      openMobileSubmenu={openMobileSubmenu}
                      setOpenMobileSubmenu={setOpenMobileSubmenu}
                      onClose={closeAllMenus}
                    />
                  ))}
                </nav>

                <div className="mt-8">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                    Besoin d’une présentation ?
                  </p>

                  <div className="mt-4 grid gap-3">
                    <button
                      type="button"
                      onClick={handleContactClick}
                      className={cx(
                        "inline-flex h-12 items-center justify-center gap-3",
                        "rounded-full bg-[#0868f2] px-5",
                        "text-sm font-semibold text-white",
                        "transition hover:bg-[#0648b8]",
                      )}
                    >
                      Parler de mon projet

                      <span className="grid h-6 w-6 place-items-center rounded-full bg-[#ff7900]">
                        <FaArrowRight className="text-[9px]" />
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={handleDemoClick}
                      className={cx(
                        "inline-flex h-12 items-center justify-center gap-2",
                        "rounded-full border border-slate-200 px-5",
                        "text-sm font-semibold text-slate-700",
                        "transition hover:border-[#0868f2]/30 hover:text-[#0868f2]",
                        "dark:border-white/10 dark:text-slate-200",
                      )}
                    >
                      <FaPlayCircle className="text-[#ff7900]" />
                      Voir la démonstration
                    </button>
                  </div>
                </div>

                {isAuthenticated ? (
                  <div className="mt-8 border-t border-slate-200 pt-6 dark:border-white/10">
                    <div className="flex items-center gap-3">
                      <UserAvatar userInfo={userInfo} />

                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-950 dark:text-white">
                          {userInfo.prenom} {userInfo.nom}
                        </p>

                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Session active
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 space-y-1">
                      {canAccessAdmin ? (
                        <Link
                          to="/appli/site-activity"
                          onClick={closeAllMenus}
                          className={cx(
                            "flex items-center gap-3 py-3",
                            "text-sm font-semibold text-slate-700",
                            "transition hover:text-[#0868f2]",
                            "dark:text-slate-200 dark:hover:text-blue-300",
                          )}
                        >
                          <FaCog />
                          Administration
                        </Link>
                      ) : null}

                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex w-full items-center gap-3 py-3 text-left text-sm font-semibold text-red-600"
                      >
                        <FaSignOutAlt />
                        Déconnexion
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="border-t border-slate-200 px-6 py-4 dark:border-white/10">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="grid h-9 w-9 place-items-center rounded-full bg-blue-50 text-[#0868f2] dark:bg-blue-500/10 dark:text-blue-300">
                      <FaUser />
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">
                        MonAppliClub
                      </p>

                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Des outils conçus pour le terrain
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={toggleDarkMode}
                    className={cx(
                      "grid h-10 w-10 shrink-0 place-items-center rounded-full",
                      "border border-slate-200 text-slate-700",
                      "transition hover:border-[#0868f2]/30 hover:text-[#0868f2]",
                      "dark:border-white/10 dark:text-white",
                    )}
                    aria-label="Changer le thème"
                  >
                    {isDarkMode ? (
                      <FaSun className="text-[#ff7900]" />
                    ) : (
                      <FaMoon />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </header>
  );
}